import { Injectable } from '@angular/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { Resource } from '@opentelemetry/resources';
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPTraceExporter } from '@nodeModules/@opentelemetry/exporter-trace-otlp-http';
import { AppConfigService } from '../appConfig/appConfigService';
import { TimberService } from '@logging/timber.service';
import { ShellContextFacade } from '../shell-context/shell-context-facade.service';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { FeatureToggleSettings } from 'app/featuresToggleSettings/featuresToggleSettings.model';
import { FeatureToggle } from '@shared/enums';
import { take } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class AppTelemetryService {
	constructor(
		private appConfigService: AppConfigService,
		private timberService: TimberService,
		private readonly featuresToggleSettingsService: FeaturesToggleSettingsService,
		private readonly shellContextFacade: ShellContextFacade
	) {}

	initialize() {
		const accessToken = this.getAccessToken();

		if (accessToken) {
			try {
				this.featuresToggleSettingsService
					.getFeaturesToggleSettings()
					.pipe(take(1))
					.subscribe({
						next: (res: FeatureToggleSettings[]) => {
							const appTelemetryFeature = res.some(
								f => f?.id === FeatureToggle?.OpenTelemetryFeatureFlag && f?.isActive === true
							);

							if (appTelemetryFeature) {
								const tracerConfig = {
									resource: new Resource({
										'service.name': this.appConfigService.appSettings?.OtelCollectoreConfig?.Appname,
										'service.version': '1.0.0',
									}),
									spanProcessor: new BatchSpanProcessor(
										new OTLPTraceExporter({
											url: this.appConfigService.appSettings?.OtelCollectoreConfig?.Endpoint,
											headers: {
												Authorization: accessToken,
											} as Record<string, string>,
										})
									),
									instrumentations: [
										new XMLHttpRequestInstrumentation({
											propagateTraceHeaderCorsUrls: [/./],
										}),
										new FetchInstrumentation({
											propagateTraceHeaderCorsUrls: [/./],
										}),
										new UserInteractionInstrumentation(),
									],
								};

								const provider = new WebTracerProvider({
									resource: tracerConfig.resource,
								});

								provider.addSpanProcessor(tracerConfig.spanProcessor);

								provider.register({
									contextManager: new ZoneContextManager(),
									propagator: new W3CTraceContextPropagator(),
								});

								registerInstrumentations({
									instrumentations: tracerConfig.instrumentations as any,
								});
							}
						},
						error: error => {
							console.log('Error initializing telemetry', error);
							this.timberService.error('Error initializing telemetry', {
								error,
								module: 'AppTelemetryService',
							});
						},
					});
			} catch (error) {
				this.timberService.error('Unexpected error in initialize()', {
					error,
					module: 'AppTelemetryService',
				});
			}
		}
	}
	private getAccessToken(): string {
		const session = this.shellContextFacade.sessionInfo;

		if (session?.accessToken) {
			return `Bearer ${session.accessToken}`;
		}

		return null;
	}
}
